/**
 * store/cues.ts — the HOT CUEs of each track: eight per track, the listener's, on the server.
 *
 * Task 60. `docs/superpowers/specs/2026-10-05-pult-design.md` «Пады (второй пакет)», stories 179,
 * 180: the pads set and take away up to eight cues in a track, and the cues are there on another
 * phone, as the place in a long track is. Its own store, like the hearts (`store/favorites.ts`):
 * the pads and the screen over them are subscribed to it, nothing that draws a list or the player.
 *
 * ## Read when a deck takes the track
 *
 * The pult reads a deck's cues ({@link loadCues}) each time the deck takes a track while it is open
 * — LOAD, the automat's next track, the pult opening on a track — so a phone sees what another
 * wrote since. Until the first answer the track's cues are `undefined`, and the pads leave them
 * alone ({@link setCue} does nothing): the server keeps the eight as one, and eight written before
 * the read would put the server's own cues out.
 *
 * ## The pad answers the finger, the server follows
 *
 * A cue set or taken away changes the eight at once, and the server gets the slots that changed —
 * only those: another phone may have set a cue since this one read, and the server keeps what a
 * write does not name. One request per track at a time, and the last change wins, as with the
 * hearts: a change while one is out only changes the eight, and when the answer comes the eight are
 * compared with what the server was last told; the slots that differ go in one more request. Three
 * pads in a row cost two writes at most, and the server ends where the listener's last finger did.
 *
 * A write that fails leaves the cues as the listener set them: they work on this phone for the
 * launch, and a cue that vanishes from under a finger mid-set is worse than one not yet on the
 * server. The next change sends the eight again, and so does the next read of the track — a read
 * never puts out a change the server has not confirmed.
 *
 * Nor one it confirmed while the read was out: the server may have answered the read before it took
 * the write, and the answer is then older than what the phone shows. The hearts keep the same
 * guard (`writtenDuringLoad`).
 */

import { create } from 'zustand';

import { fetchCues, saveCues } from '../lib/api';
import { PAD_COUNT, toMillisecond } from '../lib/pult-pads';
import type { CueSlots } from '../lib/pult-pads';

import { useSession } from './session';

export interface CuesState {
  /** Track id → its eight cues, once the server's have been read. */
  readonly byTrack: Readonly<Record<number, CueSlots>>;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Module state: nothing here is drawn, so it is not in the store
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * What the server was last told for every track with a change not yet settled: the eight before
 * the first change, then whatever the server confirmed. A track settles — leaves this map — once
 * the server has been told what the store shows.
 */
const confirmed = new Map<number, CueSlots>();
/** Tracks with a write out. */
const sending = new Set<number>();
/** Tracks with a read out, and that read. */
const reading = new Map<number, Promise<void>>();
/** Writes the server confirmed, per track: a read answered after one may predate it. */
const written = new Map<number, number>();
/** Bumped by {@link resetCues}: an answer of the page before a reset changes nothing. */
let epoch = 0;

export const useCues = create<CuesState>(() => ({ byTrack: {} }));

function token(): string | undefined {
  return useSession.getState().session?.token;
}

/** The eight of a server's answer, or `undefined` for anything that is not eight. */
function asSlots(value: unknown): CueSlots | undefined {
  if (!Array.isArray(value) || value.length !== PAD_COUNT) return undefined;
  const slots: (number | null)[] = [];
  for (const item of value as unknown[]) {
    if (item === null) slots.push(null);
    else if (typeof item === 'number' && Number.isFinite(item) && item >= 0) slots.push(item);
    else return undefined;
  }
  return slots;
}

function show(trackId: number, slots: CueSlots): void {
  useCues.setState((s) => ({ byTrack: { ...s.byTrack, [trackId]: slots } }));
}

/** The slots where `wanted` differs from `known`, by number: what a write names. */
function changedSlots(known: CueSlots, wanted: CueSlots): Record<string, number | null> {
  const changed: Record<string, number | null> = {};
  wanted.forEach((value, i) => {
    if (value !== known[i]) changed[String(i + 1)] = value;
  });
  return changed;
}

/** Sends the slots of `trackId` the server was not told yet, if any and nothing is out. */
async function settle(trackId: number): Promise<void> {
  if (sending.has(trackId)) return;
  const known = confirmed.get(trackId);
  const wanted = useCues.getState().byTrack[trackId];
  if (known === undefined || wanted === undefined) return;
  const changed = changedSlots(known, wanted);
  if (Object.keys(changed).length === 0) {
    confirmed.delete(trackId);
    return;
  }
  const mine = epoch;
  sending.add(trackId);
  const result = await saveCues(token(), trackId, changed);
  if (mine !== epoch) return;
  sending.delete(trackId);
  // A failure leaves the track unsettled: the next change or read sends again — see the header.
  if (!result.ok) return;
  confirmed.set(trackId, wanted);
  written.set(trackId, (written.get(trackId) ?? 0) + 1);
  // The listener may have pressed again while this was out.
  await settle(trackId);
}

/** Reads the server's cues of `trackId`, unless they are being read already. */
export async function loadCues(trackId: number): Promise<void> {
  const out = reading.get(trackId);
  if (out !== undefined) {
    await out;
    return;
  }
  const mine = epoch;
  const writtenBefore = written.get(trackId) ?? 0;
  const attempt = (async (): Promise<void> => {
    const result = await fetchCues(token(), trackId);
    if (mine !== epoch || !result.ok) return;
    const slots = asSlots(result.data.cues);
    if (slots === undefined) return;
    if (confirmed.has(trackId) || (written.get(trackId) ?? 0) !== writtenBefore) {
      // A change of this phone the server has not confirmed, or one it confirmed while the read was
      // out: the store keeps what it shows, and the server gets it if it does not have it.
      void settle(trackId);
      return;
    }
    show(trackId, slots);
  })();
  reading.set(trackId, attempt);
  try {
    await attempt;
  } finally {
    if (mine === epoch) reading.delete(trackId);
  }
}

/** Slot `slot` (1–8) of `trackId` becomes `seconds`, or empty with `null`. */
function change(trackId: number, slot: number, seconds: number | null): void {
  const now = useCues.getState().byTrack[trackId];
  if (now === undefined || slot < 1 || slot > PAD_COUNT) return;
  if (now[slot - 1] === seconds) return;
  if (!confirmed.has(trackId)) confirmed.set(trackId, now);
  show(
    trackId,
    now.map((value, i) => (i === slot - 1 ? seconds : value)),
  );
  void settle(trackId);
}

/** A cue at `seconds` on pad `slot` of the track — kept to the millisecond, as the server keeps it. */
export function setCue(trackId: number, slot: number, seconds: number): void {
  if (!Number.isFinite(seconds) || seconds < 0) return;
  change(trackId, slot, toMillisecond(seconds));
}

/** Pad `slot`'s cue of the track taken away. */
export function clearCue(trackId: number, slot: number): void {
  change(trackId, slot, null);
}

/**
 * No cues, with nothing in flight counted. For tests — and a second phone in them: the store is
 * module-level and outlives every screen a test renders, and a late answer must not land in the next.
 */
export function resetCues(): void {
  epoch += 1;
  confirmed.clear();
  sending.clear();
  reading.clear();
  written.clear();
  useCues.setState({ byTrack: {} }, true);
}
